import * as THREE from 'three';
import type { WeaponKind } from './WeaponSystem.js';
import {
  formatRarityWeaponName,
  RARITY_COLORS,
  RARITY_LABELS,
  rollRarity,
  type WeaponRarity,
} from './FpsWeaponRarity.js';
import { type WeaponMod } from './FpsWeaponMods.js';
import {
  applyLootBeamDistanceAlpha,
  attachRarityBeam,
  computeLootBeamOpacity,
  getRarityPresentationProfile,
} from './FpsLootPresentation.js';

export type WeaponPickupResult = { kind: WeaponKind; rarity: WeaponRarity; mod?: WeaponMod };

export type ModPickupResult = { mod: WeaponMod };

export type ModPickup = {
  mesh: THREE.Group;
  mod: WeaponMod;
  taken: boolean;
  bobPhase: number;
  beam: THREE.Mesh;
  beamMat: THREE.MeshBasicMaterial;
  label: HTMLDivElement;
};

export type WeaponPickup = {
  mesh: THREE.Group;
  kind: WeaponKind;
  rarity: WeaponRarity;
  mod?: WeaponMod;
  taken: boolean;
  bobPhase: number;
  beam: THREE.Mesh;
  beamMat: THREE.MeshBasicMaterial;
  glowBaseOpacity: number;
  glowPulseOpacity: number;
  label: HTMLDivElement;
};

const NEAR_LOOT_LABEL_CLASS = 'fps-loot-label--near';

const WEAPON_PALETTE: Record<WeaponKind, { color: number; emissive: number }> = {
  ar: { color: 0xf1c40f, emissive: 0x665500 },
  shotgun: { color: 0xbb8fce, emissive: 0x4a2860 },
  sniper: { color: 0x5dade2, emissive: 0x1a4466 },
  smg: { color: 0x58d68d, emissive: 0x1a5530 },
  pistol: { color: 0xbdc3c7, emissive: 0x444850 },
};

const WEAPON_NAMES: Record<WeaponKind, string> = {
  ar: 'Assault Rifle',
  shotgun: 'Pump Shotgun',
  sniper: 'Bolt Sniper',
  smg: 'SMG',
  pistol: 'Pistol',
};

const COLLECT_RADIUS = 1.45;
const MAGNET_DIST = 2.35;
const MAGNET_PULL_SPEED = 10;
/** Phase 30X-8: epic/legendary loot pulls harder/faster. */
const RARITY_MAGNET_MULT: Record<WeaponRarity, number> = {
  common: 1,
  uncommon: 1.08,
  rare: 1.18,
  epic: 1.42,
  legendary: 1.65,
};
const LABEL_RANGE = 4;
const BEAM_RADIUS = 0.05;
const BEAM_HEIGHT = 9;
const BEAM_OPACITY_BASE = 0.2;
const BEAM_PULSE_SPEED = 2.4;
const BEAM_PULSE_AMP = 0.08;

const MOD_CUBE_COLOR = 0xb44dff;
const MOD_CUBE_EMISSIVE = 0x6a2d8a;

export class FpsWeaponPickups {
  private readonly pickups: WeaponPickup[] = [];
  private readonly modPickups: ModPickup[] = [];
  private readonly labelRoot: HTMLDivElement;
  private readonly labelStyle: HTMLStyleElement;
  private readonly proj = new THREE.Vector3();
  private readonly beamMaterials: THREE.MeshBasicMaterial[] = [];
  private lastUpdateT = 0;

  constructor(
    _scene: THREE.Scene,
    private readonly sceneRoot: THREE.Group,
    mount: HTMLElement = document.body,
  ) {
    this.labelRoot = document.createElement('div');
    this.labelRoot.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:37;overflow:hidden';
    this.labelStyle = document.createElement('style');
    this.labelStyle.textContent = [
      '@keyframes fpsLootLabelNearPulse{0%,100%{filter:brightness(1);transform:translate(-50%,-100%) scale(1)}50%{filter:brightness(1.28);transform:translate(-50%,-100%) scale(1.045)}}',
      `.${NEAR_LOOT_LABEL_CLASS}{animation:fpsLootLabelNearPulse 920ms ease-in-out infinite}`,
    ].join('');
    mount.appendChild(this.labelStyle);
    mount.appendChild(this.labelRoot);
  }

  /** Spawn a glowing floor weapon at world x,z. Rolls rarity when omitted. */
  spawnDrop(x: number, z: number, kind: WeaponKind, rarity?: WeaponRarity, mod?: WeaponMod): void {
    const rolled = rarity ?? rollRarity();
    const beamHex = RARITY_COLORS[rolled];
    const beamColor = new THREE.Color(beamHex).getHex();
    const profile = getRarityPresentationProfile(rolled);
    const pal = WEAPON_PALETTE[kind];
    const group = new THREE.Group();
    group.position.set(x, 0.42, z);

    const bodyMat = new THREE.MeshStandardMaterial({
      color: pal.color,
      emissive: pal.emissive,
      emissiveIntensity: 0.72,
      metalness: 0.35,
      roughness: 0.28,
    });
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.14, 0.22), bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;

    const barrel = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.1, 0.1),
      bodyMat.clone(),
    );
    barrel.position.set(0.38, 0.02, 0);
    barrel.castShadow = true;

    const glow = new THREE.Mesh(
      new THREE.RingGeometry(0.28, 0.46, 20),
      new THREE.MeshBasicMaterial({
        color: beamColor,
        transparent: true,
        opacity: Math.min(0.58, profile.ringOpacity),
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = -0.08;

    const beamMat = new THREE.MeshBasicMaterial({
      color: beamColor,
      transparent: true,
      opacity: profile.beamOpacity,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    beamMat.userData.baseOpacity = profile.beamOpacity;
    beamMat.userData.pulseOpacity = profile.beamPulse;
    this.beamMaterials.push(beamMat);
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS, BEAM_HEIGHT, 12),
      beamMat,
    );
    beam.position.y = BEAM_HEIGHT * 0.5;
    beam.userData.part = 'beam';

    const labelText = formatRarityWeaponName(rolled, WEAPON_NAMES[kind]);
    const label = document.createElement('div');
    label.innerHTML = [
      `<div style="font:900 10px system-ui,sans-serif;letter-spacing:0.15em;text-transform:uppercase;color:${beamHex}">${RARITY_LABELS[rolled]}</div>`,
      `<div style="font:900 12px system-ui,sans-serif;color:#fff">${labelText}</div>`,
      `<div style="font:800 10px system-ui,sans-serif;color:#b8c4d8">Weapon pickup${mod ? ` · ${mod}` : ''}</div>`,
    ].join('');
    label.style.cssText = [
      'position:absolute',
      'display:none',
      'transform:translate(-50%,-100%)',
      'padding:6px 11px',
      'border-radius:10px',
      `background:rgba(12,10,22,0.84)`,
      `border:2px solid ${beamHex}`,
      `box-shadow:0 0 22px ${beamHex}44`,
      'font:800 11px system-ui,sans-serif',
      'letter-spacing:0.08em',
      'color:#fff',
      'text-shadow:0 1px 4px rgba(0,0,0,0.8)',
      'pointer-events:none',
      'white-space:nowrap',
    ].join(';');
    this.labelRoot.appendChild(label);

    group.add(body, barrel, glow, beam);
    attachRarityBeam(group, rolled);
    if (mod) {
      const cube = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.14, 0.14),
        new THREE.MeshStandardMaterial({
          color: MOD_CUBE_COLOR,
          emissive: MOD_CUBE_EMISSIVE,
          emissiveIntensity: 0.85,
          metalness: 0.2,
          roughness: 0.35,
        }),
      );
      cube.position.set(-0.28, 0.18, 0.12);
      cube.castShadow = true;
      group.add(cube);
    }
    this.sceneRoot.add(group);
    this.pickups.push({
      mesh: group,
      kind,
      rarity: rolled,
      mod,
      taken: false,
      bobPhase: this.pickups.length * 0.83 + Math.random() * 0.4,
      beam,
      beamMat,
      glowBaseOpacity: Math.min(0.68, profile.ringOpacity * 0.7),
      glowPulseOpacity: profile.ringPulse * 0.45,
      label,
    });
  }

  /** Standalone weapon mod floor drop — small purple cube with loot beam. */
  spawnModDrop(x: number, z: number, mod: WeaponMod): void {
    const group = new THREE.Group();
    group.position.set(x, 0.38, z);

    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.22, 0.22),
      new THREE.MeshStandardMaterial({
        color: MOD_CUBE_COLOR,
        emissive: MOD_CUBE_EMISSIVE,
        emissiveIntensity: 0.95,
        metalness: 0.25,
        roughness: 0.3,
      }),
    );
    cube.castShadow = true;
    cube.receiveShadow = true;

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.18, 0.32, 18),
      new THREE.MeshBasicMaterial({
        color: MOD_CUBE_COLOR,
        transparent: true,
        opacity: 0.45,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -0.1;

    const beamMat = new THREE.MeshBasicMaterial({
      color: MOD_CUBE_COLOR,
      transparent: true,
      opacity: BEAM_OPACITY_BASE,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.beamMaterials.push(beamMat);
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS, BEAM_HEIGHT * 0.75, 12),
      beamMat,
    );
    beam.position.y = BEAM_HEIGHT * 0.375;

    const label = document.createElement('div');
    label.textContent = 'Weapon Mod';
    label.style.cssText = [
      'position:absolute',
      'display:none',
      'transform:translate(-50%,-100%)',
      'padding:4px 10px',
      'border-radius:6px',
      'background:rgba(12,10,22,0.84)',
      'border:1px solid #b44dff',
      'font:800 11px system-ui,sans-serif',
      'letter-spacing:0.08em',
      'color:#d7aefb',
      'text-shadow:0 1px 4px rgba(0,0,0,0.8)',
      'pointer-events:none',
      'white-space:nowrap',
    ].join(';');
    this.labelRoot.appendChild(label);

    group.add(cube, ring, beam);
    attachRarityBeam(group, 'epic');
    this.sceneRoot.add(group);
    this.modPickups.push({
      mesh: group,
      mod,
      taken: false,
      bobPhase: this.modPickups.length * 1.17 + Math.random() * 0.5,
      beam,
      beamMat,
      label,
    });
  }

  update(
    timeSeconds: number,
    playerPos?: THREE.Vector3,
    camera?: THREE.Camera,
    width = 0,
    height = 0,
  ): void {
    const dt = this.lastUpdateT > 0 ? Math.min(0.05, timeSeconds - this.lastUpdateT) : 0.016;
    this.lastUpdateT = timeSeconds;
    const t = timeSeconds;
    const labelRange2 = LABEL_RANGE * LABEL_RANGE;
    const px = playerPos?.x;
    const pz = playerPos?.z;
    const canProject = !!(playerPos && camera && width > 0 && height > 0);

    for (const pick of this.pickups) {
      if (pick.taken) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const magnetMult = RARITY_MAGNET_MULT[pick.rarity] ?? 1;
      const magnetDist = MAGNET_DIST * (0.9 + magnetMult * 0.15);
      if (px != null && pz != null) {
        const dx = px - pick.mesh.position.x;
        const dz = pz - pick.mesh.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < magnetDist && dist > COLLECT_RADIUS * 0.35) {
          const pull = Math.min(
            1,
            (MAGNET_PULL_SPEED * magnetMult * dt) / Math.max(0.08, dist),
          );
          pick.mesh.position.x += dx * pull;
          pick.mesh.position.z += dz * pull;
          pick.mesh.position.y += dt * (1.8 + magnetMult * 0.4);
        }
      }

      const phase = pick.bobPhase;
      if (px == null || pz == null ||
        Math.hypot(px - pick.mesh.position.x, pz - pick.mesh.position.z) >= magnetDist) {
        pick.mesh.position.y = 0.42 + Math.sin(t * 2.6 + phase) * 0.1;
      }
      pick.mesh.rotation.y = t * 1.4 + phase;

      const glow = pick.mesh.children[2] as THREE.Mesh | undefined;
      if (glow) {
        const mat = glow.material as THREE.MeshBasicMaterial;
        mat.opacity = pick.glowBaseOpacity + Math.sin(t * 3.2 + phase) * pick.glowPulseOpacity;
      }

      const dxBeam = px != null ? px - pick.mesh.position.x : 0;
      const dzBeam = pz != null ? pz - pick.mesh.position.z : 0;
      const distBeam = px != null && pz != null ? Math.sqrt(dxBeam * dxBeam + dzBeam * dzBeam) : 52;
      const pulse = (Math.sin(t * BEAM_PULSE_SPEED + phase) + 1) * 0.5;
      if (pick.beamMat.opacity > 0) {
        pick.beamMat.opacity = computeLootBeamOpacity(pick.rarity, distBeam, pulse);
      }
      const presentation = pick.mesh.children.find((child) => child.name === 'loot-rarity-presentation');
      if (presentation) applyLootBeamDistanceAlpha(presentation, distBeam);

      if (!canProject || px == null || pz == null) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const dx = px - pick.mesh.position.x;
      const dz = pz - pick.mesh.position.z;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > labelRange2) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      this.proj.set(pick.mesh.position.x, pick.mesh.position.y + 0.55, pick.mesh.position.z);
      this.proj.project(camera!);
      if (this.proj.z > 1) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const sx = (this.proj.x * 0.5 + 0.5) * width;
      const sy = (-this.proj.y * 0.5 + 0.5) * height;
      const fade = Math.min(1, 1 - (Math.sqrt(dist2) - 2.8) / (LABEL_RANGE - 2.8));
      pick.label.style.display = 'block';
      pick.label.style.left = `${sx}px`;
      pick.label.style.top = `${sy}px`;
      pick.label.style.opacity = String(Math.max(0.35, fade));
      pick.label.classList.toggle(NEAR_LOOT_LABEL_CLASS, dist2 <= COLLECT_RADIUS * COLLECT_RADIUS * 2.25);
    }

    for (const pick of this.modPickups) {
      if (pick.taken) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const phase = pick.bobPhase;
      pick.mesh.position.y = 0.38 + Math.sin(t * 3.1 + phase) * 0.08;
      pick.mesh.rotation.y = t * 1.8 + phase;
      pick.mesh.rotation.x = Math.sin(t * 2.4 + phase) * 0.18;

      const modDx = px != null ? px - pick.mesh.position.x : 0;
      const modDz = pz != null ? pz - pick.mesh.position.z : 0;
      const modDist = px != null && pz != null ? Math.sqrt(modDx * modDx + modDz * modDz) : 52;
      const modPulse = (Math.sin(t * BEAM_PULSE_SPEED + phase) + 1) * 0.5;
      if (pick.beamMat.opacity > 0) {
        pick.beamMat.opacity = computeLootBeamOpacity('epic', modDist, modPulse);
      }
      const modPresentation = pick.mesh.children.find((child) => child.name === 'loot-rarity-presentation');
      if (modPresentation) applyLootBeamDistanceAlpha(modPresentation, modDist);

      if (!canProject || px == null || pz == null) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const dx = px - pick.mesh.position.x;
      const dz = pz - pick.mesh.position.z;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > labelRange2) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      this.proj.set(pick.mesh.position.x, pick.mesh.position.y + 0.42, pick.mesh.position.z);
      this.proj.project(camera!);
      if (this.proj.z > 1) {
        pick.label.style.display = 'none';
        pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
        continue;
      }

      const sx = (this.proj.x * 0.5 + 0.5) * width;
      const sy = (-this.proj.y * 0.5 + 0.5) * height;
      const fade = Math.min(1, 1 - (Math.sqrt(dist2) - 2.8) / (LABEL_RANGE - 2.8));
      pick.label.style.display = 'block';
      pick.label.style.left = `${sx}px`;
      pick.label.style.top = `${sy}px`;
      pick.label.style.opacity = String(Math.max(0.35, fade));
      pick.label.classList.toggle(NEAR_LOOT_LABEL_CLASS, dist2 <= COLLECT_RADIUS * COLLECT_RADIUS * 2.25);
    }
  }

  findNearestPickup(
    x: number,
    z: number,
    radius: number,
  ): { kind: WeaponKind; rarity: WeaponRarity; x: number; z: number; dist: number } | null {
    const r2 = radius * radius;
    let best: { kind: WeaponKind; rarity: WeaponRarity; x: number; z: number; dist: number } | null = null;
    for (const pick of this.pickups) {
      if (pick.taken) continue;
      const px = pick.mesh.position.x;
      const pz = pick.mesh.position.z;
      const dx = x - px;
      const dz = z - pz;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > r2) continue;
      const dist = Math.sqrt(dist2);
      if (!best || dist < best.dist) {
        best = { kind: pick.kind, rarity: pick.rarity, x: px, z: pz, dist };
      }
    }
    return best;
  }

  botTryCollectAt(x: number, z: number, radius: number): WeaponPickupResult | null {
    const r2 = radius * radius;
    for (const pick of this.pickups) {
      if (pick.taken) continue;
      const dx = x - pick.mesh.position.x;
      const dz = z - pick.mesh.position.z;
      if (dx * dx + dz * dz > r2) continue;
      pick.taken = true;
      pick.mesh.visible = false;
      pick.beamMat.opacity = 0;
      pick.label.style.display = 'none';
      pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
      return { kind: pick.kind, rarity: pick.rarity, mod: pick.mod };
    }
    return null;
  }

  tryCollect(playerPos: THREE.Vector3): WeaponPickupResult | null {
    const px = playerPos.x;
    const pz = playerPos.z;
    const r2 = COLLECT_RADIUS * COLLECT_RADIUS;
    for (const pick of this.pickups) {
      if (pick.taken) continue;
      const dx = px - pick.mesh.position.x;
      const dz = pz - pick.mesh.position.z;
      if (dx * dx + dz * dz > r2) continue;
      pick.taken = true;
      pick.mesh.visible = false;
      pick.beamMat.opacity = 0;
      pick.label.style.display = 'none';
      pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
      return { kind: pick.kind, rarity: pick.rarity, mod: pick.mod };
    }
    return null;
  }

  tryCollectMod(playerPos: THREE.Vector3): ModPickupResult | null {
    const px = playerPos.x;
    const pz = playerPos.z;
    const r2 = COLLECT_RADIUS * COLLECT_RADIUS;
    for (const pick of this.modPickups) {
      if (pick.taken) continue;
      const dx = px - pick.mesh.position.x;
      const dz = pz - pick.mesh.position.z;
      if (dx * dx + dz * dz > r2) continue;
      pick.taken = true;
      pick.mesh.visible = false;
      pick.beamMat.opacity = 0;
      pick.label.style.display = 'none';
      pick.label.classList.remove(NEAR_LOOT_LABEL_CLASS);
      return { mod: pick.mod };
    }
    return null;
  }

  dispose(): void {
    for (const pick of this.pickups) {
      pick.mesh.removeFromParent();
      pick.label.remove();
      pick.mesh.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        obj.geometry.dispose();
        const m = obj.material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      });
    }
    for (const pick of this.modPickups) {
      pick.mesh.removeFromParent();
      pick.label.remove();
      pick.mesh.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        obj.geometry.dispose();
        const m = obj.material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      });
    }
    this.pickups.length = 0;
    this.modPickups.length = 0;
    this.beamMaterials.length = 0;
    this.labelRoot.remove();
    this.labelStyle.remove();
  }
}
